import qs from 'qs';

import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import { translate } from 'services/i18nService';
import getMessagingServiceInstance, { MessagingService } from 'services/MessagingService/MessagingService';
import {
  CLOSE_PRIVATE_ROOM_DRAWER,
  FAVORITE_STATUS_RESPONSE,
  FRIEND_REQUEST_HANDLED,
  FRIEND_REQUESTS_RESPONSE,
  FRIEND_STATUS_RESPONSE,
  LIVE_TOUCH_QR_CODE_TOKEN,
  NEEDS_ID_MESSAGE_RECEIVED,
  PRIVATE_ROOM_RESPONSE,
  SET_LOADING_WEBSERVER_TIME_REQUEST,
  SET_PAUSED_REMAINING_TIME,
  STREAM_RESUME_SUCCESS,
  UI_VARIABLES_RESPONSE,
} from 'services/MessagingService/EventNames';
import NotificationService from 'services/NotificationService';
import APIStates from 'enum/APIStates';
import { BROADCASTER_LIVE_TOUCH_OPERATIONS } from 'enum/helpers';
import { FRIEND_REQUEST_ACTIONS, PAUSE_BROADCAST_OPTIONS } from 'utils/consts';
import { focusChatInputField } from 'utils/helpers';
import parseCatch from 'utils/parseCatch';
import logger from 'logger';
import { TypeOfValuesOf } from 'utils/typeHelpers';
import { User } from 'stores/UserStore/UserStore.types';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);
const WEBCHAT_HOST = ConfigurationService.get(ConfigurationKeys.WEB_CHAT_HOST);
const ADMIN_APPROVAL_SERVER = ConfigurationService.get(ConfigurationKeys.ADMIN_APPROVAL_SERVER);

const PRIVATE_ROOM_URL = `${WEBCHAT_HOST}privateRoom`;
const LIVE_TOUCH_URL = `${WEBCHAT_HOST}kiiroo`;
const FAVORITE_STATUS_URL = `${BASE_URL}/rest/v1.0/favorites/{username}/{viewerName}`;
const FRIEND_REQUESTS_URL = `${BASE_URL}/friendRequests`;
const FRIENDS_STATUS_URL = `${BASE_URL}/rest/v1.0/friends/{username}/{viewerName}`;
const PAUSE_BROADCAST_URL = `${WEBCHAT_HOST}pauseBroadcast`;
const UI_VARIABLES_URL = `${BASE_URL}/rest/v1.0/ui-variables`;
const WEBSERVER_TIMESTAMP_URL = `${WEBCHAT_HOST}webServerTimestamp`;
const NEEDS_ID_MESSAGE_URL = `${ADMIN_APPROVAL_SERVER}/rest/v1/needs-id-user-messages/{userId}`;


type PrivateRoomData = {
  accessHash: string;
  allowFriends: boolean;
  chatless: boolean;
  isAlreadyPrivateRoom: boolean;
  listInDirectory: boolean;
  password: string;
  privateMode: boolean;
  roomName: string;
  username: string
  usersString: string;
}

type LiveTouchData = {
  roomname: string;
  accessHash: string;
  operation: TypeOfValuesOf<typeof BROADCASTER_LIVE_TOUCH_OPERATIONS>;
  username: string;
}

type Operation = TypeOfValuesOf<typeof FRIEND_REQUEST_ACTIONS>;

type HandleRequestData = {
  friendUsername: User['username'];
  operation: Operation;
}

type BulkRequestParam = {
  operation: Operation
} & { [key in Operation]: boolean }

/**
 * @class handles business logic for broadcaster and expose methods
 */
export class BroadcasterService {

  constructor(public messagingService: MessagingService) {}

  /**
   * Method for updating room status from private to public and vica verse
   * @emits CLOSE_PRIVATE_ROOM_DRAWER closes private room drawer
   * @emits PRIVATE_ROOM_RESPONSE
   */
  handlePrivateRoom = (data: PrivateRoomData): void => {
    const { isAlreadyPrivateRoom } = data;
    const renderNotification = () => {
      if (data.privateMode && !isAlreadyPrivateRoom) {
        return translate('v2broadcast.room.is.private');
      }
      if (data.privateMode && isAlreadyPrivateRoom) {
        return translate('v2broadcast.changes.saved');
      }
      if (!data.privateMode) {
        return translate('v2broadcast.room.is.public');
      }

      return '';
    };

    ApiService.post(encodeURI(PRIVATE_ROOM_URL), {
      data: qs.stringify(data),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    })
      .then(() => {
        this.messagingService.emit(CLOSE_PRIVATE_ROOM_DRAWER);
        this.messagingService.emit(PRIVATE_ROOM_RESPONSE, data);
        NotificationService.create({
          type: NotificationService.type.TOAST,
          subtype: NotificationService.subtype.SUCCESS,
          text: renderNotification,
        });
      })
      .catch((err) => {
        parseCatch(err);
        NotificationService.create({
          type: NotificationService.type.TOAST,
          subtype: NotificationService.subtype.ERROR,
          text: translate('v2broadcast.twitter.send.error'),
        });
      });
  };

  /**
   * Method for handling live touch connect
   * @emits LIVE_TOUCH_QR_CODE_TOKEN emitting live touch token for qr code
   */
  handleLiveTouchConnect = (data: LiveTouchData): void => {
    ApiService.post(encodeURI(LIVE_TOUCH_URL), {
      data: qs.stringify(data),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    })
      .then((res) => {
        if (
          data.operation === BROADCASTER_LIVE_TOUCH_OPERATIONS.JOIN_ROOM ||
          data.operation === BROADCASTER_LIVE_TOUCH_OPERATIONS.LEAVE_ROOM
        ) {
          this.messagingService.emit(
            LIVE_TOUCH_QR_CODE_TOKEN,
            res.data.auth_token
          );
        }
      })
      .catch((err) => {
        parseCatch(err);
      });
  };

  /**
   * @emits SET_LOADING_WEBSERVER_TIME_REQUEST
   * @emits SET_PAUSED_REMAINING_TIME
   */
  calculateExactRemainingTime = (end: number): void => {
    this.messagingService.emit(SET_LOADING_WEBSERVER_TIME_REQUEST, APIStates.PENDING);
    ApiService.get(encodeURI(WEBSERVER_TIMESTAMP_URL))
      .then((res) => {
        const remainingMilliseconds = end - res.data;
        this.messagingService.emit(SET_PAUSED_REMAINING_TIME, remainingMilliseconds);
        this.messagingService.emit(SET_LOADING_WEBSERVER_TIME_REQUEST, APIStates.LOADED);
      })
      .catch((error) => parseCatch(error));
  };

  /**
   * Method for getting friend requests
   * @emits FRIEND_REQUESTS_RESPONSE
   */
  getFriendRequests = (): void => {
    ApiService.get(FRIEND_REQUESTS_URL, { params: { limit: 20, offset: 0 } })
      .then((res) => {
        this.messagingService.emit(FRIEND_REQUESTS_RESPONSE, res.data.response.received);
      })
      .catch((err) => parseCatch(err));
  };

  /**
   * Method for handling friend request
   * @emits FRIEND_REQUEST_HANDLED
   */
  handleFriendRequest = (data: HandleRequestData): void => {
    const { operation } = data;
    const params = { ...data };
    const isBulkAction = [
      FRIEND_REQUEST_ACTIONS.ACCEPT_ALL,
      FRIEND_REQUEST_ACTIONS.IGNORE_ALL,
    ].includes(operation);
    const MILLIS = 2000;
    if (isBulkAction) {
      delete params.friendUsername;
      (params as unknown as BulkRequestParam)[operation] = true;
    }

    ApiService.post(FRIEND_REQUESTS_URL, {
      data: qs.stringify(params),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    })
      .then(() => {
        setTimeout(() => this.messagingService.emit(FRIEND_REQUEST_HANDLED, params), MILLIS);
      })
      .catch((err) => parseCatch(err));
  };

  /**
   * Method for checking friend status with viewer
   * @emits FRIEND_STATUS_RESPONSE handles response
   */
  getPerformerFriendsStatus = (username: User['username'], viewerName: User['username']): void => {
    const callUrl = FRIENDS_STATUS_URL.replace('{username}', username).replace('{viewerName}', viewerName);
    ApiService.get(encodeURI(callUrl))
      .then((res) => this.messagingService.emit(FRIEND_STATUS_RESPONSE, res.data))
      .catch((error) => parseCatch(error));
  };

  /**
   * Method for checking favorite status with viewer
   * @emits FAVORITE_STATUS_RESPONSE handles response
   */
  getPerformerFavoriteStatus = (username: User['username'], viewerName: User['username']): void => {
    const callUrl = FAVORITE_STATUS_URL.replace('{username}', username).replace('{viewerName}', viewerName);
    ApiService.get(encodeURI(callUrl))
      .then((res) => this.messagingService.emit(FAVORITE_STATUS_RESPONSE, res.data))
      .catch((error) => parseCatch(error));
  };

  /**
   * Method for resuming broadcast
   * @emits STREAM_RESUME_SUCCESS handles response
   */
  resumeBroadcast = (username: User['username'], accessHash: string): void => {
    const payload = {
      action: PAUSE_BROADCAST_OPTIONS.RESUME,
      accessHash,
      username,
    };

    ApiService.post(PAUSE_BROADCAST_URL, {
      data: qs.stringify(payload),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    })
      .then(() => {
        this.messagingService.emit(STREAM_RESUME_SUCCESS);
        focusChatInputField();
      })
      .catch((error) => parseCatch(error));
  };

  /**
   * Method for getting ui variables
   * @emits UI_VARIABLES_RESPONSE handles response
   */
  getUiVariables = (): void => {
    ApiService.get(UI_VARIABLES_URL)
      .then((res) => {
        this.messagingService.emit(UI_VARIABLES_RESPONSE, res.data);
      })
      .catch((error) => {
        parseCatch(error);
      });
  };

  /**
   * @emits NEEDS_ID_MESSAGE_RECEIVED
   */
  getNeedsIdMessage = (user: User): void => {
    const { userId, username, accessHash } = user;
    const url = ApiService.substitute(
      NEEDS_ID_MESSAGE_URL,
      { userId: userId.toString() }
    );
    const headers = { username, accessHash };

    ApiService.get(url, { headers, withCredentials: false })
      .then((response) =>
        this.messagingService.emit(NEEDS_ID_MESSAGE_RECEIVED, response.data)
      )
      .catch((error) => {
        logger.error(error);
      });
  };
}

export default new BroadcasterService(getMessagingServiceInstance());
